import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { QuillModule, QuillEditorComponent } from 'ngx-quill';
import Quill from 'quill';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { CommunityService } from '../../services/community.service';
import { CommunityMentionableUser, communityErrorKey } from '../../models/community.models';
import { CommunityUploadUrlService } from '../../services/community-upload-url.service';
import './quill-divider-blot';

const EMOJIS = [
  '😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇',
  '🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚',
  '😋','😛','😝','😜','🤪','🤨','🧐','🤓','😎','🥳',
  '😏','😒','😞','😔','😟','😕','🙁','☹️','😣','😖',
  '😫','😩','🥺','😢','😭','😤','😠','😡','🤬','🤯',
  '😳','🥵','🥶','😱','😨','😰','😥','😓','🤗','🤔',
  '🤭','🤫','🤥','😶','😐','😑','😬','🙄','😯','😦',
  '👍','👎','👏','🙌','👐','🤝','🙏','💪','✌️','🤞',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔',
  '🔥','✨','🎉','🎊','💯','✅','❌','⚠️','❓','❗'
];

/**
 * The picture the writer has clicked, and what the size buttons need to act on
 * it: where it sits in the document, and its intrinsic width — the presets are a
 * share of the ORIGINAL, so pressing "50%" twice does not keep halving.
 */
interface SelectedImage {
  index: number;
  naturalWidth: number;
  width: number | null;
}

/** Range in the plain text that a `@query` mention trigger occupies. */
interface MentionRange {
  start: number;
  end: number;
}

@Component({
  selector: 'app-community-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslocoModule, QuillModule],
  templateUrl: './community-editor.component.html',
  styleUrl: './community-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommunityEditorComponent {
  /**
   * The body to edit, in its STORED form. Uploaded images are switched to their
   * display form on the way in and back on the way out (see
   * CommunityUploadUrlService): the stored `/api/media/upload/…` src resolves
   * against the PAGE origin, so inside the editor it renders as a broken image
   * on every build where the app and the API are not the same origin.
   */
  @Input() set value(v: string) {
    this.content = this.uploadUrls.htmlToDisplay(v ?? '');
  }
  @Input() placeholder = '';
  @Input() minHeight = 120;

  /** The channel this post/thread is being written in — required for mentions, uploads and video embeds. */
  @Input() channelId: number | null = null;
  /** Channel rights (`permissions.mention/upload_image/upload_file/embed_video`); every extra stays hidden/inert without them. */
  @Input() canMention = false;
  @Input() canUploadImage = false;
  @Input() canUploadFile = false;
  @Input() canEmbedVideo = false;

  @Output() valueChange = new EventEmitter<string>();
  /** Translation key of a failed upload/embed/mention request, for the host page's error alert. */
  @Output() uploadError = new EventEmitter<string>();

  @ViewChild('quillRef') quillRef?: QuillEditorComponent;
  @ViewChild('imageInput') imageInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('pdfInput') pdfInputRef?: ElementRef<HTMLInputElement>;

  content = '';
  emojiPickerOpen = signal(false);
  emojis = EMOJIS;

  /** Set while an attachment/embed request is in flight (disables the toolbar buttons). */
  busy = signal(false);

  // --- Mentions (§12) ----------------------------------------------------------
  mentionOpen = signal(false);
  mentionLoading = signal(false);
  mentionResults = signal<CommunityMentionableUser[]>([]);
  private mentionRange: MentionRange | null = null;
  private mentionQuery$ = new Subject<string>();

  // --- Image scaling --------------------------------------------------------------
  /** Share of the original width the buttons offer; 100 removes the attribute again. */
  readonly imageWidths = [25, 50, 75, 100];
  selectedImage = signal<SelectedImage | null>(null);

  // --- Video embed prompt --------------------------------------------------------
  videoPromptOpen = signal(false);
  videoUrlDraft = '';

  private communityService = inject(CommunityService);
  private uploadUrls = inject(CommunityUploadUrlService);
  private destroyRef = inject(DestroyRef);

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ header: 3 }, { header: 4 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['blockquote', 'link']
    ],
    clipboard: {
      // Strip images, scripts, tables etc. on paste by matching against allowed formats only
      matchVisual: false,
      // Pasted material (a PDF, a Word document, a web page) brings whatever heading
      // levels its author used, but CommunitySanitizer::ALLOWED_TAGS keeps only h3/h4
      // — a pasted <h1> would reach the server as a heading and come back as bare
      // text. Rather than lose the structure, every level is folded onto the two we
      // do store: h1/h2 become h3, h5/h6 become h4.
      matchers: [
        ['h1, h2', CommunityEditorComponent.clampHeading(3)],
        ['h5, h6', CommunityEditorComponent.clampHeading(4)]
      ]
    }
  };

  /**
   * Rewrites the `header` attribute the built-in matchers already put on the
   * block. User matchers run after those, so the level is simply overwritten —
   * no need to rebuild the delta.
   */
  private static clampHeading(level: number) {
    return (_node: Node, delta: { ops?: { attributes?: Record<string, unknown> }[] }) => {
      delta.ops?.forEach(op => {
        if (op.attributes?.['header']) op.attributes['header'] = level;
      });
      return delta;
    };
  }

  // 'image'/'video' are inserted via clipboard.dangerouslyPasteHTML (bypasses this
  // allowlist entirely), but declaring them here lets Quill keep them intact
  // through the writer's later normal edits instead of stripping them as unknown.
  // 'width' is deliberately NOT listed: it is not a registered format but an
  // attribute the Image blot handles in its own `format()`, and EmbedBlot.formatAt
  // calls that directly without consulting the registry. Listing it would only
  // make Quill log "Cannot register \"width\"" on every editor.
  // Mirrors CommunitySanitizer::ALLOWED_TAGS: a format missing here is thrown away
  // on paste, one the sanitizer does not allow is thrown away on save. `header` and
  // `blockquote` were allowed by the backend all along but stripped by this list,
  // so a pasted document arrived with its structure flattened.
  formats = ['bold', 'italic', 'underline', 'header', 'blockquote', 'divider', 'list', 'link', 'image', 'video'];

  constructor() {
    this.mentionQuery$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap(query => {
          if (!this.channelId || !this.canMention) return of<CommunityMentionableUser[]>([]);
          this.mentionLoading.set(true);
          return this.communityService.mentionable(this.channelId, query).pipe(
            map(res => res.data),
            catchError(() => of<CommunityMentionableUser[]>([]))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(results => {
        this.mentionLoading.set(false);
        this.mentionResults.set(results);
      });
  }

  /**
   * Quill hands the editor over once it exists. Clicking a picture selects it and
   * opens the size buttons — a click listener rather than `selection-change`,
   * because clicking an embed does not reliably leave a one-character selection
   * behind, and the DOM node is what carries `naturalWidth` anyway.
   */
  onEditorCreated(editor: any): void {
    editor.root.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!(target instanceof HTMLImageElement)) {
        this.selectedImage.set(null);
        return;
      }
      const blot = Quill.find(target);
      if (!blot) return;
      const index = editor.getIndex(blot as any);
      editor.setSelection(index, 1, 'user');
      const width = parseInt(target.getAttribute('width') ?? '', 10);
      this.selectedImage.set({
        index,
        naturalWidth: target.naturalWidth || target.width,
        width: Number.isFinite(width) ? width : null
      });
    });
  }

  /** Which preset the selected image currently sits on, for the pressed state. */
  activeImageWidth(): number | null {
    const selected = this.selectedImage();
    if (!selected) return null;
    if (selected.width === null) return 100;
    if (!selected.naturalWidth) return null;
    const percent = Math.round((selected.width / selected.naturalWidth) * 100);
    return this.imageWidths.find(w => Math.abs(w - percent) <= 2) ?? null;
  }

  /**
   * Scales the selected image. 100% removes the attribute rather than writing the
   * original width out: an image with no width stays responsive, and the backend
   * drops out-of-range values anyway (CommunitySanitizer::IMAGE_MIN/MAX_WIDTH).
   */
  setImageWidth(percent: number): void {
    const editor = this.quillRef?.quillEditor;
    const selected = this.selectedImage();
    if (!editor || !selected) return;

    const width = percent >= 100 || !selected.naturalWidth
      ? null
      : Math.max(1, Math.round((selected.naturalWidth * percent) / 100));
    editor.formatText(selected.index, 1, 'width', width === null ? '' : String(width), 'user');
    this.selectedImage.set({ ...selected, width });
  }

  onContentChange(event: { html: string | null }): void {
    const html = event.html ?? '';
    this.content = html;
    this.emitValue(html);
    this.detectMentionTrigger();
  }

  /** Pages of the last imported PDF, shown as a note under the editor. */
  pdfImported = signal<{ pages: number; truncated: boolean } | null>(null);

  triggerPdfPicker(): void {
    this.pdfInputRef?.nativeElement.click();
  }

  /**
   * Converts a PDF into editable markup and inserts it at the caret. The document
   * itself is never stored — this is the "paste a document" path, for material
   * whose formatting the clipboard throws away (a PDF viewer puts plain text on
   * it: no headings, no sizes).
   */
  onPdfPicked(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file || !this.channelId) return;

    this.busy.set(true);
    this.pdfImported.set(null);
    this.communityService.importPdf(this.channelId, file).subscribe({
      next: res => {
        this.busy.set(false);
        if (res.html) this.insertHtml(res.html);
        this.pdfImported.set({ pages: res.pages, truncated: res.truncated });
      },
      error: err => {
        this.busy.set(false);
        this.uploadError.emit(communityErrorKey(err));
      }
    });
  }

  /** Inserts a horizontal rule at the caret — the manual counterpart to a pasted one. */
  insertDivider(): void {
    this.insertHtml('<hr>');
  }

  toggleEmojiPicker(): void {
    this.emojiPickerOpen.update(v => !v);
  }

  insertEmoji(emoji: string): void {
    const editor = this.quillRef?.quillEditor;
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, emoji, 'user');
    editor.setSelection(index + emoji.length, 0, 'user');
    this.emojiPickerOpen.set(false);
  }

  // --- Mentions ------------------------------------------------------------------

  /** Detects a trailing `@` + 2+ characters right before the caret and (re)queries suggestions. */
  private detectMentionTrigger(): void {
    if (!this.canMention || !this.channelId) return;
    const editor = this.quillRef?.quillEditor;
    const selection = editor?.getSelection();
    if (!editor || !selection) {
      this.closeMentions();
      return;
    }
    const textBefore = editor.getText(0, selection.index);
    const match = /(?:^|\s)@([\p{L}\p{N}._-]{2,32})$/u.exec(textBefore);
    if (!match) {
      this.closeMentions();
      return;
    }
    const term = match[1];
    this.mentionRange = { start: selection.index - term.length - 1, end: selection.index };
    this.mentionOpen.set(true);
    this.mentionQuery$.next(term);
  }

  private closeMentions(): void {
    if (this.mentionOpen()) this.mentionOpen.set(false);
    this.mentionResults.set([]);
    this.mentionRange = null;
  }

  insertMention(user: CommunityMentionableUser): void {
    const editor = this.quillRef?.quillEditor;
    const range = this.mentionRange;
    if (!editor || !range) return;
    editor.deleteText(range.start, range.end - range.start, 'user');
    const text = `@${user.username} `;
    editor.insertText(range.start, text, 'user');
    editor.setSelection(range.start + text.length, 0, 'user');
    this.closeMentions();
  }

  // --- Attachments (§14) --------------------------------------------------------

  triggerImagePicker(): void {
    this.imageInputRef?.nativeElement.click();
  }

  triggerFilePicker(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onImagePicked(event: Event): void {
    this.handleUpload(event, 'image');
  }

  onFilePicked(event: Event): void {
    this.handleUpload(event, 'file');
  }

  private handleUpload(event: Event, kind: 'image' | 'file'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    if (!file || !this.channelId) return;

    this.busy.set(true);
    this.communityService.createAttachment(this.channelId, kind, file).subscribe({
      next: res => {
        this.busy.set(false);
        // The image goes in as a display URL so the writer sees the picture; the
        // link keeps the stored path, which the browser resolves fine on click.
        const html = kind === 'image'
          ? `<img src="${this.escapeAttr(this.uploadUrls.toDisplay(res.attachment.url))}">`
          : `<a href="${this.escapeAttr(res.attachment.url)}">${this.escapeText(res.attachment.name)}</a>`;
        this.insertHtml(html);
      },
      error: err => {
        this.busy.set(false);
        this.uploadError.emit(communityErrorKey(err));
      }
    });
  }

  // --- Video embed (§14, F9) ------------------------------------------------------

  toggleVideoPrompt(): void {
    this.videoPromptOpen.update(v => !v);
    this.videoUrlDraft = '';
  }

  submitVideoEmbed(): void {
    const url = (this.videoUrlDraft || '').trim();
    if (!url || !this.channelId) return;
    this.busy.set(true);
    this.communityService.resolveEmbed(this.channelId, url).subscribe({
      next: res => {
        this.busy.set(false);
        this.videoPromptOpen.set(false);
        this.videoUrlDraft = '';
        this.insertHtml(res.html);
      },
      error: err => {
        this.busy.set(false);
        this.uploadError.emit(communityErrorKey(err));
      }
    });
  }

  // --- Shared helpers --------------------------------------------------------------

  /** Inserts raw HTML at the caret. Bypasses the `formats` allowlist entirely (Quill's own escape hatch for exactly this). */
  private insertHtml(html: string): void {
    const editor = this.quillRef?.quillEditor;
    if (!editor) return;
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.clipboard.dangerouslyPasteHTML(index, html, 'user');
    this.content = editor.root.innerHTML;
    this.emitValue(this.content);
  }

  /** Hands the body back in its STORED form — the only one the backend accepts. */
  private emitValue(html: string): void {
    this.valueChange.emit(this.uploadUrls.htmlToStored(html));
  }

  private escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  private escapeText(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
}
